import { MdLightbulb, MdTrendingUp, MdWarning } from "react-icons/md";
import Badge from "components/badge";
import Card, { CardHeader } from "components/card";
import { getArchetype } from "lib/archetypes";

const PAIRING_SUGGESTIONS = {
  visionary: "organizer",
  organizer: "visionary",
  shepherd: "evangelist",
  evangelist: "shepherd",
  teacher: "servant",
  servant: "teacher",
};

const RADIUS = 45;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const scoreStatus = (score) => {
  if (score >= 75) return { tone: "success", label: "Well Balanced", ring: "stroke-success" };
  if (score >= 50) return { tone: "warning", label: "Moderately Balanced", ring: "stroke-warning" };
  return { tone: "danger", label: "Needs Balance", ring: "stroke-danger" };
};

const archetypeLabel = (key) => getArchetype(key)?.label || key;

const article = (word) => (/^[aeiou]/i.test(word) ? "an" : "a");

const Section = ({ icon: Icon, title, className, children }) => (
  <div>
    <h3 className={`mb-2 flex items-center gap-2 text-sm font-semibold ${className}`}>
      <Icon aria-hidden="true" />
      <span>{title}</span>
    </h3>
    <ul className="space-y-1 text-sm text-fg-muted">{children}</ul>
  </div>
);

const TeamBalanceInsights = ({ teamBalance, config }) => {
  if (!teamBalance) return null;

  const { labels } = config;
  const { balanceScore = 0, gaps = [], overrepresented = [] } = teamBalance;
  const status = scoreStatus(balanceScore);
  const offset = CIRCUMFERENCE - (balanceScore / 100) * CIRCUMFERENCE;
  const pairings = gaps.filter((gap) => PAIRING_SUGGESTIONS[gap]);

  return (
    <Card padding="md">
      <CardHeader title="Team Balance Insights" className="mb-4" />

      <div className="flex flex-col items-center gap-6 lg:flex-row lg:items-start">
        <div className="flex flex-col items-center gap-2">
          <div className="relative h-28 w-28">
            <svg className="h-full w-full -rotate-90" viewBox="0 0 100 100" aria-hidden="true">
              <circle
                cx="50"
                cy="50"
                r={RADIUS}
                fill="none"
                stroke="currentColor"
                strokeWidth="8"
                className="text-line"
              />
              <circle
                cx="50"
                cy="50"
                r={RADIUS}
                fill="none"
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={CIRCUMFERENCE}
                strokeDashoffset={offset}
                className={status.ring}
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-2xl font-bold text-fg">
              {balanceScore}
            </span>
          </div>
          <p className="text-sm font-medium text-fg-muted">Balance Score</p>
          <Badge tone={status.tone} dot>
            {status.label}
          </Badge>
        </div>

        <div className="flex-1 space-y-4">
          {gaps.length > 0 && (
            <Section icon={MdWarning} title="Underrepresented Archetypes" className="text-danger-fg">
              {gaps.map((gap) => (
                <li key={gap}>
                  {archetypeLabel(gap)}: consider recruiting or developing {labels.pluralLower} in
                  this area.
                </li>
              ))}
            </Section>
          )}

          {overrepresented.length > 0 && (
            <Section icon={MdTrendingUp} title="Overrepresented Archetypes" className="text-warning-fg">
              {overrepresented.map((arch) => (
                <li key={arch}>{archetypeLabel(arch)}</li>
              ))}
            </Section>
          )}

          {pairings.length > 0 && (
            <Section icon={MdLightbulb} title="Pairing Suggestions" className="text-info-fg">
              {pairings.map((gap) => {
                const gapLabel = archetypeLabel(gap);
                const pairLabel = archetypeLabel(PAIRING_SUGGESTIONS[gap]);
                return (
                  <li key={gap}>
                    Consider pairing {article(gapLabel)}{" "}
                    <span className="font-medium text-fg">{gapLabel}</span> with {article(pairLabel)}{" "}
                    <span className="font-medium text-fg">{pairLabel}</span>.
                  </li>
                );
              })}
            </Section>
          )}

          {gaps.length === 0 && overrepresented.length === 0 && (
            <p className="text-sm text-fg-muted">
              Your team has a well-balanced composition across all archetypes.
            </p>
          )}
        </div>
      </div>
    </Card>
  );
};

export default TeamBalanceInsights;
